import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import { IconChartPie, IconTarget, IconTrendingUp } from '../components/Icons';
import { API_BASE_URL } from '../../config';

// ── Types ──────────────────────────────────────────────────────────────────────

interface FactorRow {
  name: string;
  key: string;
  exposure: number;
  description: string;
  tStat: number;
}
interface FactorContribution { factor: string; contribution: number; }
interface TStats {
  alpha: number; beta: number; smb: number; hml: number;
  mom: number; rmw: number; cma: number;
}
interface AnalysisResults {
  label: string;
  factors: FactorRow[];
  contributions: FactorContribution[];
  rSquared: number;
  annualizedAlpha: number;
  dataPoints: number;
  tStats: TStats;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const FACTOR_DESCRIPTIONS: Record<string, string> = {
  'Market Beta':          'Sensitivity to broad market (S&P 500) moves',
  'Size (SMB)':           'Small-cap tilt (+) vs large-cap (−)',
  'Value (HML)':          'Value tilt (+) vs growth (−)',
  'Momentum (MOM)':       'Exposure to recent 12-month price momentum',
  'Profitability (RMW)':  'Robust operating profit tilt (+) vs weak (−)',
  'Investment (CMA)':     'Conservative investment style (+) vs aggressive (−)',
};

// ── Sub-components ────────────────────────────────────────────────────────────

const exposureToWidth = (val: number) =>
  (Math.abs(Math.min(2, Math.max(-2, val))) / 2) * 100;

const ExposureBar: React.FC<{ exposure: number }> = ({ exposure }) => {
  const isPos = exposure >= 0;
  const w = exposureToWidth(exposure);
  return (
    <div className="flex items-center gap-1 w-full">
      <div className="flex-1 flex justify-end h-2.5">
        {!isPos && <div className="h-full rounded-l bg-red-500 opacity-80" style={{ width: `${w}%` }} />}
      </div>
      <div className="w-px h-3 bg-slate-400 flex-shrink-0" />
      <div className="flex-1 h-2.5">
        {isPos && <div className="h-full rounded-r bg-blue-500 opacity-80" style={{ width: `${w}%` }} />}
      </div>
    </div>
  );
};

const StyleBox: React.FC<{ smb: number; hml: number }> = ({ smb, hml }) => {
  const activeRow = smb >= 0 ? 'small' : 'large';
  const activeCol = hml >= 0 ? 'value' : 'growth';
  const cells = [
    { row: 'small', col: 'value',  label: 'Small Value'  },
    { row: 'small', col: 'growth', label: 'Small Growth' },
    { row: 'large', col: 'value',  label: 'Large Value'  },
    { row: 'large', col: 'growth', label: 'Large Growth' },
  ];
  return (
    <div>
      <p className="text-xs font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
        <IconTarget size={13} className="text-amber-500" /> Style Box
      </p>
      <div className="text-[10px] text-slate-400 text-center mb-1">Small ↑</div>
      <div>
        <div className="flex justify-between text-[10px] text-slate-400 mb-1 px-1">
          <span>← Value</span><span>Growth →</span>
        </div>
        <div className="grid grid-cols-2 gap-1">
          {cells.map(cell => {
            const isActive = cell.row === activeRow && cell.col === activeCol;
            return (
              <div
                key={cell.label}
                className={`w-24 h-14 flex items-center justify-center rounded border text-xs font-medium
                  ${isActive
                    ? 'bg-amber-100 border-amber-400 text-amber-700 font-bold'
                    : 'bg-slate-50 border-slate-200 text-slate-400'}`}
              >
                {cell.label}
              </div>
            );
          })}
        </div>
      </div>
      <div className="text-[10px] text-slate-400 text-center mt-1">↓ Large</div>
    </div>
  );
};

const TStatBadge: React.FC<{ t: number }> = ({ t }) => {
  const abs = Math.abs(t);
  const sig = abs >= 2.0 ? '★★' : abs >= 1.65 ? '★' : '';
  const color = abs >= 2.0 ? 'text-emerald-600' : abs >= 1.65 ? 'text-amber-600' : 'text-slate-400';
  return (
    <span className={`text-[10px] font-mono ${color} ml-1`} title={`t = ${t.toFixed(2)}`}>
      {sig || `t=${t.toFixed(1)}`}
    </span>
  );
};

// ── Main View ─────────────────────────────────────────────────────────────────

const FactorExposureView: React.FC = () => {
  const [ticker, setTicker]       = useState('');
  const [results, setResults]     = useState<AnalysisResults | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError]         = useState<string | null>(null);

  const run = async () => {
    const key = ticker.trim().toUpperCase();
    if (!key) return;
    setIsAnalyzing(true);
    setResults(null);
    setError(null);

    try {
      const res = await fetch(`${API_BASE_URL}/aiq/factor-exposure/${key}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Analysis failed');

      const ts: TStats = data.t_stats;
      setResults({
        label:            `${data.ticker} — FF6 Regression (${data.data_points} days)`,
        rSquared:         data.r_squared,
        annualizedAlpha:  data.annualized_alpha_pct,
        dataPoints:       data.data_points,
        tStats:           ts,
        factors: [
          { name: 'Market Beta',         key: 'beta', exposure: data.factors.beta, description: FACTOR_DESCRIPTIONS['Market Beta'],         tStat: ts.beta },
          { name: 'Size (SMB)',          key: 'smb',  exposure: data.factors.smb,  description: FACTOR_DESCRIPTIONS['Size (SMB)'],          tStat: ts.smb  },
          { name: 'Value (HML)',         key: 'hml',  exposure: data.factors.hml,  description: FACTOR_DESCRIPTIONS['Value (HML)'],         tStat: ts.hml  },
          { name: 'Momentum (MOM)',      key: 'mom',  exposure: data.factors.mom,  description: FACTOR_DESCRIPTIONS['Momentum (MOM)'],      tStat: ts.mom  },
          { name: 'Profitability (RMW)', key: 'rmw',  exposure: data.factors.rmw,  description: FACTOR_DESCRIPTIONS['Profitability (RMW)'], tStat: ts.rmw  },
          { name: 'Investment (CMA)',    key: 'cma',  exposure: data.factors.cma,  description: FACTOR_DESCRIPTIONS['Investment (CMA)'],    tStat: ts.cma  },
        ],
        contributions: data.contributions,
      });
    } catch (err: any) {
      setError(err.message || 'Analysis failed');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const smb = results?.factors.find(f => f.key === 'smb')?.exposure ?? 0;
  const hml = results?.factors.find(f => f.key === 'hml')?.exposure ?? 0;

  return (
    <div className="flex h-full bg-slate-50">

      {/* ── Left Panel ─────────────────────────────────────────────────── */}
      <div className="w-96 flex-shrink-0 bg-white border-r border-slate-200 flex flex-col overflow-y-auto custom-scrollbar">

        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 bg-amber-50">
          <div className="flex items-center gap-2 mb-1">
            <IconChartPie className="text-amber-600" size={18} />
            <h2 className="text-base font-bold text-slate-800">Factor Exposure</h2>
          </div>
          <p className="text-xs text-slate-500">Fama-French 6-Factor OLS Regression · live market data</p>
        </div>

        {/* Input */}
        <div className="p-5 border-b border-slate-100 space-y-3">
          <input
            type="text"
            value={ticker}
            onChange={e => setTicker(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && run()}
            placeholder="Ticker symbol (e.g. AAPL)"
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 uppercase"
          />
          <div className="flex gap-2">
            {['AAPL', 'NVDA', 'MSFT', 'SPY'].map(p => (
              <button
                key={p}
                onClick={() => setTicker(p)}
                className="px-2.5 py-1 text-xs rounded bg-slate-100 text-slate-500 hover:bg-amber-50 hover:text-amber-700 border border-slate-200 hover:border-amber-300 transition-colors"
              >
                {p}
              </button>
            ))}
          </div>
          <button
            onClick={run}
            disabled={isAnalyzing || !ticker.trim()}
            className="w-full py-2.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-semibold text-sm rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {isAnalyzing
              ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Analyzing...</>
              : 'Run Analysis'}
          </button>
          {error && (
            <p className="text-xs text-red-500 bg-red-50 rounded-lg px-3 py-2 border border-red-100">{error}</p>
          )}
        </div>

        {/* Factor loadings list */}
        {results && (
          <div className="p-5 flex-1">
            <p className="text-xs font-bold text-amber-600 uppercase tracking-wide mb-1">Factor Loadings</p>
            <p className="text-sm font-semibold text-slate-700 mb-4">{results.label}</p>
            <div className="flex justify-between text-[10px] text-slate-400 mb-1 px-0.5">
              <span>−2</span><span>0</span><span>+2</span>
            </div>
            <div className="space-y-4">
              {results.factors.map(f => (
                <div key={f.key}>
                  <div className="flex justify-between items-baseline mb-1">
                    <span className="text-xs font-medium text-slate-700 flex items-center">
                      {f.name}
                      <TStatBadge t={f.tStat} />
                    </span>
                    <span className={`text-xs font-mono font-bold ${f.exposure >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                      {f.exposure >= 0 ? '+' : ''}{f.exposure.toFixed(2)}
                    </span>
                  </div>
                  <ExposureBar exposure={f.exposure} />
                  <p className="text-[10px] text-slate-400 mt-0.5 leading-tight">{f.description}</p>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 mt-5 leading-snug">
              ★★ significant (|t|≥2.0) &nbsp;·&nbsp; ★ marginal (|t|≥1.65)
            </p>
          </div>
        )}

        {!results && !isAnalyzing && (
          <div className="flex-1 flex items-center justify-center p-8 text-center">
            <div>
              <IconChartPie className="text-slate-200 mx-auto mb-3" size={36} />
              <p className="text-sm text-slate-400">Enter a ticker and click <span className="font-semibold text-amber-600">Run Analysis</span>.</p>
              <p className="text-xs text-slate-300 mt-2 leading-snug">
                Uses ~3 years of daily returns regressed against Fama-French 5-factor + momentum data.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ── Right Panel ────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        {results ? (
          <div className="p-6 space-y-6">

            {/* Style box + top-4 KPI cards */}
            <div className="flex gap-6">
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex-shrink-0">
                <StyleBox smb={smb} hml={hml} />
              </div>
              <div className="flex-1 grid grid-cols-2 gap-3">
                {results.factors.slice(0, 4).map(f => (
                  <div key={f.key} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                    <p className="text-xs text-slate-500 mb-1">{f.name}</p>
                    <p className={`text-2xl font-bold font-mono ${f.exposure >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                      {f.exposure >= 0 ? '+' : ''}{f.exposure.toFixed(2)}
                    </p>
                    <div className="mt-2"><ExposureBar exposure={f.exposure} /></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Regression Quality */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <p className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-1.5">
                <IconTarget size={14} className="text-amber-500" /> Regression Quality
              </p>
              <div className="grid grid-cols-3 gap-4 mb-5">
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-700">{(results.rSquared * 100).toFixed(1)}%</p>
                  <p className="text-xs text-slate-400 mt-0.5">R² Explained</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className={`text-2xl font-bold ${results.annualizedAlpha >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {results.annualizedAlpha >= 0 ? '+' : ''}{results.annualizedAlpha.toFixed(1)}%
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">Ann. Alpha</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-600">{results.dataPoints}</p>
                  <p className="text-xs text-slate-400 mt-0.5">Trading Days</p>
                </div>
              </div>
              {/* t-stat summary row */}
              <div className="grid grid-cols-6 gap-2">
                {results.factors.map(f => {
                  const abs = Math.abs(f.tStat);
                  const bg = abs >= 2.0 ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                           : abs >= 1.65 ? 'bg-amber-50 border-amber-200 text-amber-700'
                           : 'bg-slate-50 border-slate-200 text-slate-400';
                  return (
                    <div key={f.key} className={`text-center p-2 rounded border text-[10px] font-mono ${bg}`}>
                      <p className="font-bold">{f.tStat > 0 ? '+' : ''}{f.tStat.toFixed(1)}</p>
                      <p className="text-[9px] mt-0.5">{f.key.toUpperCase()}</p>
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-slate-400 mt-2">t-statistics per factor loading · green = significant at 5%</p>
            </div>

            {/* Factor return contribution chart */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <p className="text-sm font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
                <IconTrendingUp size={14} className="text-amber-500" /> Annualised Factor Return Contribution
              </p>
              <p className="text-xs text-slate-400 mb-4">
                Estimated annual return (%) attributable to each factor loading × factor mean return
              </p>
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={results.contributions} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                    <XAxis dataKey="factor" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false}
                      tickFormatter={v => `${v > 0 ? '+' : ''}${v.toFixed(1)}%`} />
                    <ReferenceLine y={0} stroke="#e2e8f0" />
                    <Tooltip
                      contentStyle={{ borderRadius: '8px', fontSize: '12px' }}
                      formatter={(v: any) => [`${Number(v) > 0 ? '+' : ''}${Number(v).toFixed(2)}%`, 'Contribution']}
                    />
                    <Bar dataKey="contribution" radius={[3, 3, 0, 0]}>
                      {results.contributions.map((e, i) => (
                        <Cell key={i} fill={e.contribution >= 0 ? '#10b981' : '#ef4444'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

          </div>
        ) : (
          <div className="flex-1 h-full flex items-center justify-center">
            <div className="text-center max-w-sm p-8">
              <div className="w-16 h-16 bg-amber-50 border border-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <IconChartPie className="text-amber-400" size={28} />
              </div>
              <p className="text-base font-semibold text-slate-600 mb-1">No Analysis Yet</p>
              <p className="text-sm text-slate-400">
                Enter a ticker (AAPL, SPY, NVDA…) and click <span className="font-semibold text-amber-600">Run Analysis</span>.
              </p>
              <p className="text-xs text-slate-300 mt-3 leading-snug">
                Runs an OLS regression of daily excess returns against Fama-French FF5 + Momentum factors.
                Factor data is sourced from Ken French's public data library; price history from Yahoo Finance.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FactorExposureView;
